import { Link } from 'react-router-dom';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { ArrowLeft, Shield, Brain, ShieldAlert, CreditCard, ShieldOff, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { EmailTester } from '@/components/admin/EmailTester';
import { EmailEventsLog } from '@/components/admin/EmailEventsLog';
import { CreditPurchasesLog } from '@/components/admin/CreditPurchasesLog';
import { CreditTransactionsLog } from '@/components/admin/CreditTransactionsLog';
import { DangerZone } from '@/components/admin/DangerZone';
import { PricingSettings } from '@/components/admin/PricingSettings';
import { TestClockManager } from '@/components/admin/TestClockManager';
import { TestClockSettings } from '@/components/admin/TestClockSettings';
import { CheckoutSettings } from '@/components/admin/CheckoutSettings';
import { AdminSecuritySettings } from '@/components/admin/AdminSecuritySettings';
import { AIPromptsManager } from '@/components/admin/AIPromptsManager';
import { TestUsersManager } from '@/components/admin/TestUsersManager';
import { UserRolesManager } from '@/components/admin/UserRolesManager';
import { RateLimitMonitor } from '@/components/admin/RateLimitMonitor';
import { StripePriceSync } from '@/components/admin/StripePriceSync';
import { ContentFilterLog } from '@/components/admin/ContentFilterLog';

export default function Admin() {
  return (
    <div className="min-h-screen flex flex-col app-surface">
      <main className="flex-1 py-8 bg-dot-grid">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="mb-8 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="app-icon-box">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                  Admin Dashboard
                </h1>
                <p className="text-sm text-muted-foreground">Management and Testing Tools</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LogoutButton />
              <Button asChild variant="outline">
                <Link to="/settings" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Settings
                </Link>
              </Button>
            </div>
          </div>

          <Tabs defaultValue="email-testing" className="space-y-6">
            <div className="app-tile p-2">
              <TabsList className="flex flex-wrap w-full h-auto gap-1 bg-transparent p-0">
                <TabsTrigger value="email-testing">Email Testing</TabsTrigger>
                <TabsTrigger value="email-log">Email Log</TabsTrigger>
                <TabsTrigger value="credits">Credits</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                <TabsTrigger value="test-clock">Test Clock</TabsTrigger>
                <TabsTrigger value="test-clock-manager">Test Clock Manager</TabsTrigger>
                <TabsTrigger value="checkout">Checkout</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
                <TabsTrigger value="ai-prompts" className="gap-1.5">
                  <Brain className="h-3.5 w-3.5" />
                  AI Prompts
                </TabsTrigger>
                <TabsTrigger value="rate-limits" className="gap-1.5">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Rate Limits
                </TabsTrigger>
                <TabsTrigger value="danger" className="text-destructive data-[state=active]:text-destructive">
                  Danger
                </TabsTrigger>
                <TabsTrigger value="users" className="gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Test Users
                </TabsTrigger>
                <TabsTrigger value="user-roles" className="gap-1.5">
                  <Shield className="h-3.5 w-3.5" />
                  User Roles
                </TabsTrigger>
                <TabsTrigger value="stripe-sync" className="gap-1.5">
                  <CreditCard className="h-3.5 w-3.5" />
                  Stripe Sync
                </TabsTrigger>
                <TabsTrigger
                  value="content-filter"
                  className="gap-1.5 text-destructive data-[state=active]:text-destructive"
                >
                  <ShieldOff className="h-3.5 w-3.5" />
                  Content Filter
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="email-testing"><EmailTester /></TabsContent>
            <TabsContent value="email-log"><EmailEventsLog /></TabsContent>
            <TabsContent value="credits" className="space-y-6">
              <CreditPurchasesLog />
              <CreditTransactionsLog />
            </TabsContent>
            <TabsContent value="pricing"><PricingSettings /></TabsContent>
            <TabsContent value="test-clock"><TestClockSettings /></TabsContent>
            <TabsContent value="test-clock-manager"><TestClockManager /></TabsContent>
            <TabsContent value="checkout"><CheckoutSettings /></TabsContent>
            <TabsContent value="security"><AdminSecuritySettings /></TabsContent>
            <TabsContent value="ai-prompts"><AIPromptsManager /></TabsContent>
            <TabsContent value="rate-limits"><RateLimitMonitor /></TabsContent>
            <TabsContent value="danger"><DangerZone /></TabsContent>
            <TabsContent value="users"><TestUsersManager /></TabsContent>
            <TabsContent value="user-roles"><UserRolesManager /></TabsContent>
            <TabsContent value="stripe-sync"><StripePriceSync /></TabsContent>
            <TabsContent value="content-filter"><ContentFilterLog /></TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
